import { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { CaretLeft, CaretRight, CalendarBlank, MapPin, Users, Warning, MapTrifold } from "@phosphor-icons/react";
import { toast } from "sonner";
import { computeRowBars, rowGridTemplateRows, BAR_HEIGHT_PX } from "../lib/calendarLayout";
import ComplianceBadge from "../components/ComplianceBadge";
import useComplianceBadges from "../hooks/useComplianceBadges";
import OperativeRouteDialog from "../components/OperativeRouteDialog";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
// Local-date YYYY-MM-DD formatter. We deliberately avoid `.toISOString()` here
// because it converts to UTC and shifts the date by ±1 day for any user not
// in UTC (e.g. UK BST = UTC+1 → "Apr 15 00:00 BST" becomes "Apr 14 23:00 UTC"
// → returns "2026-04-14" instead of "2026-04-15"). This caused the dispatch
// board to display every job under the wrong day column.
function fmtISODate(d) {
  const x = startOfDay(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
}
function startOfWeek(d) { const x = startOfDay(d); const dow = (x.getDay() + 6) % 7; return addDays(x, -dow); }

const STATUS_BG = {
  pending: "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/40 dark:text-amber-100",
  scheduled: "bg-blue-100 text-blue-900 border-blue-300 dark:bg-blue-900/40 dark:text-blue-100",
  in_progress: "bg-indigo-100 text-indigo-900 border-indigo-300 dark:bg-indigo-900/40 dark:text-indigo-100",
  completed: "bg-green-100 text-green-900 border-green-300 dark:bg-green-900/40 dark:text-green-100",
  cancelled: "bg-slate-200 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300",
};

function pillClass(status, emergency) {
  if (emergency) return "bg-red-100 text-red-900 border-red-300 dark:bg-red-900/40 dark:text-red-100";
  return STATUS_BG[status] || STATUS_BG.scheduled;
}

export default function DispatchBoardPage() {
  const [view, setView] = useState("week"); // 'day' | 'week'
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [operatives, setOperatives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [targetOperativeId, setTargetOperativeId] = useState(null);

  const { rangeStart, rangeEnd, days, title } = useMemo(() => {
    if (view === "day") {
      return {
        rangeStart: anchor, rangeEnd: anchor, days: [anchor],
        title: anchor.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" }),
      };
    }
    const s = startOfWeek(anchor);
    const e = addDays(s, 6);
    return {
      rangeStart: s, rangeEnd: e,
      days: Array.from({ length: 7 }, (_, i) => addDays(s, i)),
      title: `${s.toLocaleDateString(undefined, { day: "numeric", month: "short" })} – ${e.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`,
    };
  }, [view, anchor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/in-house/dispatch-schedule", {
        params: { start: fmtISODate(rangeStart), end: fmtISODate(rangeEnd) },
      });
      setOperatives(data.operatives || []);
    } catch {
      toast.error("Could not load dispatch schedule");
    } finally {
      setLoading(false);
    }
  }, [rangeStart, rangeEnd]);

  useEffect(() => { load(); }, [load]);

  // Collect every "job" id across all operatives so we can fetch the
  // compliance ack summary in one round-trip and feed the shield badge
  // on each chip.
  const jobIdsForBadges = useMemo(() => {
    const ids = [];
    for (const op of operatives) {
      for (const j of (op.jobs || [])) ids.push(j.id);
    }
    return Array.from(new Set(ids));
  }, [operatives]);
  const complianceByJob = useComplianceBadges(jobIdsForBadges);

  const onReassign = useCallback(async ({ entity_type, entity_id, new_operative_id, from_operative_id }) => {
    try {
      const { data } = await API.post("/in-house/dispatch-reassign", { entity_type, entity_id, new_operative_id, from_operative_id });
      if (data.unchanged) {
        toast("Already assigned to this operative.");
        return;
      }
      toast.success(`Reassigned to ${data.new_operative.name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reassign failed");
    }
  }, [load]);

  const step = (dir) => {
    if (view === "day") setAnchor(addDays(anchor, dir));
    else setAnchor(addDays(anchor, dir * 7));
  };

  const flatTotal = operatives.reduce((sum, op) => sum + op.total_count, 0);

  // Route preview dialog — defaults to the anchor day (day view) or today when
  // it falls inside the visible week, otherwise the week's first day.
  const [routeDlg, setRouteDlg] = useState({ open: false, operative: null });
  const routeInitialDate = useMemo(() => {
    if (view === "day") return fmtISODate(anchor);
    const todayIso = fmtISODate(new Date());
    const dayIsos = days.map(fmtISODate);
    return dayIsos.includes(todayIso) ? todayIso : dayIsos[0];
  }, [view, anchor, days]);

  return (
    <div className="p-6 space-y-6" data-testid="dispatch-board">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Users className="h-7 w-7" /> Dispatch Board
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Team-wide schedule across all in-house operatives.
            <span className="ml-2 font-medium">{operatives.length} operative{operatives.length === 1 ? "" : "s"} · {flatTotal} job{flatTotal === 1 ? "" : "s"} in view</span>
          </p>
        </div>
      </div>

      <div className="border border-border bg-card rounded-lg">
        <div className="flex items-center justify-between flex-wrap gap-3 px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <CalendarBlank className="h-5 w-5 text-slate-500" />
            <h2 className="text-base font-semibold" data-testid="dispatch-title">{title}</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 border border-border rounded-md p-0.5">
              {["day", "week"].map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  data-testid={`dispatch-view-${v}`}
                  className={`text-xs px-2 py-1 rounded capitalize ${view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                >
                  {v}
                </button>
              ))}
            </div>
            <Button size="sm" variant="ghost" onClick={() => step(-1)} data-testid="dispatch-prev"><CaretLeft size={14} /></Button>
            <Button size="sm" variant="outline" className="text-xs" onClick={() => setAnchor(startOfDay(new Date()))} data-testid="dispatch-today">Today</Button>
            <Button size="sm" variant="ghost" onClick={() => step(1)} data-testid="dispatch-next"><CaretRight size={14} /></Button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-sm text-slate-400">Loading dispatch schedule…</div>
        ) : operatives.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            No active in-house operatives yet. Add some via Settings → In-House Team.
          </div>
        ) : (
          <>
            <div className="px-4 py-2 border-b border-border bg-blue-50 dark:bg-blue-950/30 text-xs text-blue-800 dark:text-blue-200" data-testid="dispatch-dnd-hint">
              <span className="font-semibold">Tip:</span> drag any job chip onto a different operative's row to reassign it.
            </div>
            <DispatchGrid days={days} operatives={operatives} onReassign={onReassign} dragState={{ targetOperativeId, setTargetOperativeId }} complianceByJob={complianceByJob} onShowRoute={(op) => setRouteDlg({ open: true, operative: op })} />
          </>
        )}
      </div>

      <OperativeRouteDialog
        open={routeDlg.open}
        onOpenChange={(o) => setRouteDlg((s) => ({ ...s, open: o }))}
        operative={routeDlg.operative}
        initialDate={routeInitialDate}
      />
    </div>
  );
}

function DispatchGrid({ days, operatives, onReassign, dragState, complianceByJob, onShowRoute }) {
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[800px]">
        {/* Header row */}
        <div
          className="grid border-b border-border bg-muted/40"
          style={{ gridTemplateColumns: `200px repeat(${days.length}, minmax(160px, 1fr))` }}
        >
          <div className="px-3 py-2 text-[10px] uppercase tracking-wide text-muted-foreground font-semibold border-r border-border">Operative</div>
          {days.map((d) => {
            const isToday = fmtISODate(d) === fmtISODate(new Date());
            return (
              <div
                key={fmtISODate(d)}
                className={`px-3 py-2 text-[10px] uppercase tracking-wide font-semibold border-r border-border last:border-r-0 ${isToday ? "text-primary" : "text-muted-foreground"}`}
              >
                {WEEKDAYS[(d.getDay() + 6) % 7]} {d.getDate()}
              </div>
            );
          })}
        </div>
        {/* Operative rows */}
        {operatives.map((op) => (
          <OperativeRow
            key={op.id}
            op={op}
            days={days}
            onReassign={onReassign}
            draggable
            dragState={dragState}
            complianceByJob={complianceByJob}
            onShowRoute={onShowRoute}
          />
        ))}
      </div>
    </div>
  );
}

function OperativeRow({ op, days, onReassign, draggable, dragState, complianceByJob = {}, onShowRoute }) {
  // Build barEvents: each job/emergency gets a startISO/endISO so the bar
  // engine can lay out one continuous bar across its full date span.
  const barEvents = useMemo(() => {
    const flat = [
      ...op.jobs.map((j) => ({ ...j, entity_type: "job", emergency: false })),
      ...op.emergency_jobs.map((j) => ({ ...j, entity_type: "emergency_job", emergency: true })),
    ];
    return flat.map((it) => {
      const startISO = it.scheduled_start ? it.scheduled_start.slice(0, 10) : null;
      let endISO = startISO;
      if (it.scheduled_end) endISO = it.scheduled_end.slice(0, 10);
      else if (it.schedule?.end_date) endISO = it.schedule.end_date;
      return {
        id: `${it.entity_type}-${it.id}`,
        startISO,
        endISO: endISO || startISO,
        startTime: it.scheduled_start ? (it.scheduled_start.slice(11, 16) || "") : "",
        item: it,
      };
    }).filter(b => b.startISO);
  }, [op]);

  const rowDays = useMemo(() => days.map(d => fmtISODate(d)), [days]);
  const { bars, maxSlot } = useMemo(() => computeRowBars(rowDays, barEvents), [rowDays, barEvents]);
  // Cap visible slots to keep the row at a reasonable height.
  const MAX_VISIBLE = 8;
  const visible = bars.filter(b => b.slot < MAX_VISIBLE);
  const hidden = new Map(rowDays.map(d => [d, 0]));
  bars.forEach(b => {
    if (b.slot < MAX_VISIBLE) return;
    for (let c = b.startCol - 1; c < b.startCol - 1 + b.span; c++) {
      hidden.set(rowDays[c], (hidden.get(rowDays[c]) || 0) + 1);
    }
  });
  const slotsForGrid = Math.max(1, Math.min(MAX_VISIBLE, maxSlot + 1));

  const initials = (op.name || "?").split(" ").slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  const isDropTarget = dragState && dragState.targetOperativeId === op.id;

  // Single grid that matches the header column template exactly so columns
  // align perfectly. Bars are positioned in the day columns (cols 2..N+1).
  return (
    <div
      className={`grid border-b border-border last:border-b-0 hover:bg-muted/20 transition-colors relative ${isDropTarget ? "bg-blue-50 dark:bg-blue-900/30 ring-2 ring-primary ring-inset" : ""}`}
      style={{
        gridTemplateColumns: `200px repeat(${days.length}, minmax(160px, 1fr))`,
        gridTemplateRows: rowGridTemplateRows(slotsForGrid, 0),
      }}
      data-testid={`dispatch-row-${op.id}`}
      onDragOver={draggable ? (e) => {
        e.preventDefault();
        dragState && dragState.setTargetOperativeId(op.id);
      } : undefined}
      onDragLeave={draggable ? () => dragState && dragState.targetOperativeId === op.id && dragState.setTargetOperativeId(null) : undefined}
      onDrop={draggable ? async (e) => {
        e.preventDefault();
        const raw = e.dataTransfer.getData("application/json");
        dragState && dragState.setTargetOperativeId(null);
        if (!raw) return;
        try {
          const { entity_type, entity_id, source_operative_id } = JSON.parse(raw);
          if (source_operative_id === op.id) return;
          await onReassign({ entity_type, entity_id, new_operative_id: op.id, from_operative_id: source_operative_id });
        } catch { /* ignore */ }
      } : undefined}
    >
      {/* Operative name — spans the whole row vertically */}
      <div
        style={{ gridColumn: 1, gridRow: `1 / -1` }}
        className="px-3 py-3 border-r border-border flex items-center gap-2 min-w-0"
      >
        <div className="h-7 w-7 rounded-full bg-primary/10 text-primary text-[11px] font-semibold flex items-center justify-center shrink-0">
          {initials || "?"}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{op.name}</p>
          <p className="text-[10px] text-muted-foreground">{op.total_count} job{op.total_count === 1 ? "" : "s"}</p>
        </div>
        {onShowRoute && (
          <button
            onClick={() => onShowRoute(op)}
            title="View planned day route"
            className="shrink-0 text-slate-400 hover:text-blue-600 transition-colors p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-950/40"
            data-testid={`dispatch-route-btn-${op.id}`}
          >
            <MapTrifold className="h-4 w-4" weight="duotone" />
          </button>
        )}
      </div>
      {/* Day cell backgrounds */}
      {days.map((d, i) => {
        const iso = fmtISODate(d);
        const hiddenCount = hidden.get(iso) || 0;
        return (
          <div
            key={iso}
            style={{ gridColumn: `${i + 2}`, gridRow: `1 / -1` }}
            className="px-2 py-2 border-r border-border last:border-r-0 min-h-[68px] relative"
          >
            {hiddenCount > 0 && (
              <p className="absolute bottom-1 left-2 text-[10px] text-muted-foreground font-medium pointer-events-none">+{hiddenCount} more</p>
            )}
          </div>
        );
      })}
      {/* Continuous bars — columns offset by +1 because the operative cell
         occupies grid column 1. */}
      {visible.map((b) => (
        <DispatchBarChip
          key={b.event.id}
          item={b.event.item}
          bar={b}
          dayColumnOffset={1}
          sourceOperativeId={op.id}
          draggable={draggable}
          complianceSummary={b.event.item?.entity_type === "job" ? complianceByJob[b.event.item.id] : null}
        />
      ))}
    </div>
  );
}

function DispatchBarChip({ item, bar, dayColumnOffset = 0, sourceOperativeId, draggable, complianceSummary }) {
  // scheduled_start comes back as `YYYY-MM-DDTHH:MM:SS+00:00` — slice the HH:MM
  // straight out rather than round-tripping through Date+toLocaleTimeString,
  // which silently shifts to the browser's local TZ (e.g. UK BST = +1h off).
  const time = (item.scheduled_start && typeof item.scheduled_start === "string" && item.scheduled_start.length >= 16)
    ? item.scheduled_start.slice(11, 16) : "";
  const addr = [item.street_address, item.town_city].filter(Boolean).join(", ") || item.property_name || "";
  const href = item.entity_type === "emergency_job" ? `/emergency-jobs/${item.id}` : `/jobs/${item.id}`;
  return (
    <Link
      to={href}
      draggable={draggable}
      onDragStart={draggable ? (e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("application/json", JSON.stringify({
          entity_type: item.entity_type,
          entity_id: item.id,
          source_operative_id: sourceOperativeId,
        }));
      } : undefined}
      className={`flex items-center gap-1.5 border rounded px-2 ${pillClass(item.status, item.emergency)} hover:ring-1 hover:ring-primary/50 transition ${draggable ? "cursor-grab active:cursor-grabbing" : ""}`}
      style={{
        gridColumn: `${bar.startCol + dayColumnOffset} / span ${bar.span}`,
        gridRow: 2 + bar.slot,
        height: `${BAR_HEIGHT_PX - 2}px`,
        marginLeft: bar.continuesLeft ? 0 : 3,
        marginRight: bar.continuesRight ? 0 : 3,
        marginTop: 1,
        alignSelf: "start",
        position: "relative",
        zIndex: 5,
      }}
      data-testid={`dispatch-chip-${item.id}${bar.isMultiDay ? "-bar" : ""}`}
      title={`${item.title}${bar.isMultiDay ? ` (${bar.clampedStartISO} → ${bar.clampedEndISO})` : ""}${addr ? ` · ${addr}` : ""}`}
    >
      {item.emergency && <Warning size={10} className="shrink-0" />}
      {bar.continuesLeft && <span className="opacity-60 shrink-0">‹</span>}
      {time && <span className="text-[11px] font-semibold shrink-0">{time}</span>}
      <span className="text-[11px] font-semibold truncate flex-1">{item.title}</span>
      <CrewAvatars item={item} highlightId={sourceOperativeId} />
      <ComplianceBadge summary={complianceSummary} jobId={item.id} size={10} />
      {!bar.isMultiDay && addr && (
        <span className="flex items-center gap-0.5 text-[10px] opacity-70 truncate shrink-0 max-w-[40%]">
          <MapPin size={9} className="shrink-0" />
          <span className="truncate">{addr}</span>
        </span>
      )}
      {bar.continuesRight && <span className="opacity-60 shrink-0">›</span>}
    </Link>
  );
}

const AVATAR_TINTS = ["bg-amber-500", "bg-sky-500", "bg-emerald-500", "bg-rose-500", "bg-violet-500", "bg-teal-500"];

/** Stacked initials for every operative on a crew job (2+). The row's own operative gets a ring so it's clear who else is coming. */
function CrewAvatars({ item, highlightId, max = 3 }) {
  const ids = item.assigned_operative_ids || [];
  const names = (item.assigned_operative_names && item.assigned_operative_names.length)
    ? item.assigned_operative_names
    : (item.assigned_operative_name || "").split(",").map(n => n.trim()).filter(Boolean);
  if (names.length < 2) return null;
  const initials = (n) => n.split(" ").slice(0, 2).map(w => w[0] || "").join("").toUpperCase();
  const shown = names.slice(0, max);
  return (
    <span className="flex items-center -space-x-1.5 shrink-0" title={`Crew of ${names.length}: ${names.join(", ")}`} data-testid={`dispatch-crew-${item.id}`}>
      {shown.map((n, i) => (
        <span key={`${n}-${i}`}
          className={`h-4 w-4 rounded-full ${AVATAR_TINTS[i % AVATAR_TINTS.length]} text-white text-[7px] font-bold flex items-center justify-center border border-white dark:border-slate-900 ${ids[i] && ids[i] === highlightId ? "ring-1 ring-offset-0 ring-slate-900 dark:ring-white" : ""}`}
          data-testid={`dispatch-crew-avatar-${item.id}-${i}`}>
          {initials(n)}
        </span>
      ))}
      {names.length > max && (
        <span className="h-4 min-w-4 px-0.5 rounded-full bg-slate-600 text-white text-[7px] font-bold flex items-center justify-center border border-white dark:border-slate-900">+{names.length - max}</span>
      )}
    </span>
  );
}
